import { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import apiServices from '../../services/service';
import { Ic } from '../../shared/component/GosIcon';
import AddUiPermissionsForm from './addUiPermissionsForm';

/* GrowthOS — UI Permissions (design: growthos/AccessPermissions.jsx →
   UIPermissions, the record-list variant). Header + toolbar (search + Module/Type
   filters) + table (Module / Type / Parent / Permission Value / Display Name /
   Active / Edit). Rebuilt from the legacy AG-grid page and wired to the REAL APIs
   (getUiPermissionList, getUiPermissionsType/Module/Parent, addUiPermissions,
   updateUiPermissions). The add/edit MODAL reuses the existing AddUiPermissionsForm
   component (it owns the real dependent-dropdown + validation + payload logic — the
   same "reuse the module's own form" approach as Bucket Management). There is NO
   delete-UI-permission API, so the design's rows are Edit-only. Active toggles
   inline through the same real edit path (updateUiPermissions). Reuses the shared
   .gos-tm scope — no new CSS. Renders in-shell via the `embedded` prop. */

const AVA_COLORS = ['#c96342', '#2e63a6', '#6a4fae', '#2c7a45', '#b34480', '#9a751c', '#4f6b8f', '#0e8c6f', '#2f7ea3', '#a35a2f'];
const hashIdx = (s, n) => {
  let h = 0;
  for (let i = 0; i < (s || '').length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % n;
};
const tintFor = (s) => AVA_COLORS[hashIdx(String(s || ''), AVA_COLORS.length)];

const typeIcon = (t) => {
  const n = String(t || '').toLowerCase();
  if (n === 'menu') return 'list';
  if (n === 'field') return 'edit';
  return 'grid'; // Screen
};

const gosSelect = {
  control: (base, s) => ({
    ...base, minHeight: 38, borderRadius: 10, fontSize: 13,
    borderColor: s.isFocused ? 'var(--gos-accent)' : 'var(--gos-border-strong)',
    boxShadow: s.isFocused ? '0 0 0 3px var(--gos-accent-soft)' : 'none',
    backgroundColor: 'var(--gos-surface)', '&:hover': { borderColor: 'var(--gos-accent)' },
  }),
  menu: (b) => ({ ...b, zIndex: 9999, backgroundColor: 'var(--gos-surface)', border: '1px solid var(--gos-border-strong)', borderRadius: 11, overflow: 'hidden' }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  option: (b, s) => ({ ...b, fontSize: 13, backgroundColor: s.isSelected ? 'var(--gos-accent-soft)' : s.isFocused ? 'var(--gos-surface-3)' : 'transparent', color: s.isSelected ? 'var(--gos-accent-ink)' : 'var(--gos-text)' }),
  placeholder: (b) => ({ ...b, color: 'var(--gos-text-3)' }),
  singleValue: (b) => ({ ...b, color: 'var(--gos-text)' }),
  input: (b) => ({ ...b, color: 'var(--gos-text)' }),
  indicatorSeparator: (b) => ({ ...b, backgroundColor: 'var(--gos-border)' }),
  dropdownIndicator: (b) => ({ ...b, color: 'var(--gos-text-3)' }),
};

/* self-contained mini switch (no new CSS) */
const MiniSwitch = ({ on, onClick, title }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    style={{
      width: 34, height: 20, borderRadius: 999, border: 'none', cursor: 'pointer', padding: 0,
      background: on ? '#2c7a45' : 'var(--gos-border-strong)', position: 'relative', transition: 'background .15s',
    }}
  >
    <span style={{ position: 'absolute', top: 2, left: on ? 16 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
  </button>
);

const initialValue = {
  permissionValue: '',
  permissionDisplayName: '',
  permissionTypeId: '',
  permissionParentId: null,
  moduleId: '',
  isActive: null,
};

function UiPermissionsList({ embedded = false }) {
  const Wrap = ({ children }) => (embedded ? <>{children}</> : <div className="inner-page">{children}</div>);

  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [modFilter, setModFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);

  const [open, setOpen] = useState(false);
  const [uiPermissionData, setUiPermissionData] = useState();
  const [permissionType, setPermissionType] = useState([]);
  const [permissionModule, setPermissionModule] = useState([]);
  const [permissionParent, setPermissionParent] = useState([]);

  const loadList = () => {
    setLoading(true);
    apiServices
      .getUiPermissionList()
      .then((res) => setRowData(res.data?.uiPermissionList || []))
      .catch((e) => console.error('getUiPermissionList', e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadList();
    apiServices.getUiPermissionsType().then((r) => setPermissionType(r.data?.authReferenceLookups || [])).catch((e) => console.error('getUiPermissionsType', e));
    apiServices.getUiPermissionsModule().then((r) => setPermissionModule(r.data?.authReferenceLookups || [])).catch((e) => console.error('getUiPermissionsModule', e));
    apiServices.getUiPermissionsParent().then((r) => setPermissionParent(r.data?.uiPermissionList || [])).catch((e) => console.error('getUiPermissionsParent', e));
  }, []);

  const handleOpenAddForm = () => {
    setUiPermissionData({ ...initialValue, type: 'add' });
    setOpen(true);
  };
  const handleClickOpenEditForm = (row) => {
    setUiPermissionData({ ...row, type: 'edit' });
    setOpen(true);
  };
  const handleCloseAddForm = () => setOpen(false);

  const handleFormSubmit = (formData) => {
    setLoading(true);
    if (formData.type === 'edit') {
      const {
        type, moduleId, moduleName, permissionParentId, permissionParentName,
        permissionTypeId, permissionTypeName, permissionValue, ...updatedFormData
      } = formData;
      apiServices
        .updateUiPermissions(updatedFormData)
        .then((response) => {
          if (response.status === 200) {
            setOpen(false);
            toast.success('Permissions updated successfully!', { position: 'top-right' });
            loadList();
          } else {
            Object.keys(response.errors || {}).forEach((k) => (response.errors[k] || []).forEach((m) => toast.error(m, { position: 'top-right' })));
            setLoading(false);
          }
        })
        .catch(() => { setLoading(false); toast.error('An unexpected error occurred.', { position: 'top-right' }); });
    } else {
      apiServices
        .addUiPermissions(formData)
        .then((response) => {
          if (response.status === 200) {
            setOpen(false);
            toast.success('Permissions created successfully!', { position: 'top-right' });
            loadList();
          } else {
            setOpen(false);
            Object.keys(response.errors || {}).forEach((k) => (response.errors[k] || []).forEach((m) => toast.error(m, { position: 'top-right' })));
            setLoading(false);
          }
        })
        .catch(() => { setLoading(false); toast.error('An unexpected error occurred.', { position: 'top-right' }); });
    }
  };

  // inline Active toggle routes through the same real edit path (updateUiPermissions)
  const toggleActive = (row) => handleFormSubmit({ ...row, type: 'edit', isActive: !row.isActive });

  const moduleOptions = useMemo(() => {
    const names = [...new Set((rowData || []).map((r) => r.moduleName).filter(Boolean))].sort();
    return names.map((n) => ({ value: n, label: n }));
  }, [rowData]);
  const typeOptions = useMemo(() => {
    const names = [...new Set((rowData || []).map((r) => r.permissionTypeName).filter(Boolean))].sort();
    return names.map((n) => ({ value: n, label: n }));
  }, [rowData]);

  const rows = useMemo(() => {
    let r = rowData || [];
    const s = q.trim().toLowerCase();
    if (s) r = r.filter((p) => `${p.moduleName || ''} ${p.permissionParentName || ''} ${p.permissionValue || ''} ${p.permissionDisplayName || ''}`.toLowerCase().includes(s));
    if (modFilter) r = r.filter((p) => p.moduleName === modFilter.value);
    if (typeFilter) r = r.filter((p) => p.permissionTypeName === typeFilter.value);
    return r;
  }, [rowData, q, modFilter, typeFilter]);

  return (
    <Wrap>
      <div className="gos-tm">
        <div className="gos-tm-head">
          <div>
            <div className="gos-tm-title">UI Permissions</div>
            <div className="gos-tm-sub">Screens and menus that make up the interface. Grant these to roles under Teams &amp; Permissions.</div>
          </div>
          <button type="button" className="gos-btn gos-btn-primary" onClick={handleOpenAddForm}>
            <Ic name="plus" size={16} />New UI Permission
          </button>
        </div>

        <div className="gos-tm-cardbox" style={{ overflow: 'visible', padding: 0 }}>
          <div className="gos-tm-toolbar" style={{ padding: '14px 16px', borderBottom: '1px solid var(--gos-border)', margin: 0 }}>
            <div className="gos-tm-search">
              <Ic name="search" size={16} />
              <input placeholder="Search in UI Permissions…" value={q} onChange={(e) => setQ(e.target.value)} />
              {q ? <button type="button" className="gos-tm-clear" onClick={() => setQ('')}><Ic name="x" size={14} /></button> : null}
            </div>
            <span className="gos-tm-count"><strong>{rows.length}</strong> permissions</span>
            <div className="gos-tm-grow" />
            <div style={{ width: 190 }}>
              <Select options={moduleOptions} value={modFilter} onChange={setModFilter} placeholder="All modules" isClearable menuPortalTarget={document.body} styles={gosSelect} />
            </div>
            <div style={{ width: 150 }}>
              <Select options={typeOptions} value={typeFilter} onChange={setTypeFilter} placeholder="All types" isClearable menuPortalTarget={document.body} styles={gosSelect} />
            </div>
          </div>

          {loading ? (
            <div className="gos-tm-tblscroll">
              <table className="gos-tm-tbl">
                <tbody>
                  {Array.from({ length: 7 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 6 }).map((__, c) => (
                        <td key={c}><span className="gos-skel-an" style={{ display: 'block', width: `${75 - c * 8}%`, height: 10, borderRadius: 5 }} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <>
              <div className="gos-tm-tblscroll">
                <table className="gos-tm-tbl">
                  <thead>
                    <tr>
                      <th>Module</th><th>Type</th><th>Parent</th><th>Permission Value</th><th>Display Name</th>
                      <th style={{ textAlign: 'center', width: 80 }}>Active</th>
                      <th style={{ textAlign: 'center', width: 56 }}>Edit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.permissionId || r.id || r.permissionValue} style={r.isActive ? undefined : { opacity: 0.55 }}>
                        <td>
                          <span className="gos-tm-row" style={{ gap: 8 }}>
                            <span style={{ width: 9, height: 9, borderRadius: 3, flexShrink: 0, background: tintFor(r.moduleName) }} />
                            <span className="gos-tm-cell">{r.moduleName || '—'}</span>
                          </span>
                        </td>
                        <td><span className="gos-tm-modchip"><Ic name={typeIcon(r.permissionTypeName)} size={12} />{r.permissionTypeName || '—'}</span></td>
                        <td>{r.permissionParentName ? <span className="gos-tm-cell gos-tm-muted">{r.permissionParentName}</span> : <span className="gos-tm-muted">—</span>}</td>
                        <td><span className="gos-tm-mono">{r.permissionValue || '—'}</span></td>
                        <td><span className="gos-tm-cellname">{r.permissionDisplayName || '—'}</span></td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{ display: 'inline-flex', justifyContent: 'center' }}>
                            <MiniSwitch on={!!r.isActive} onClick={() => toggleActive(r)} title={r.isActive ? 'Active — click to deactivate' : 'Inactive — click to activate'} />
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <button type="button" className="gos-tm-icon-btn" title="Edit permission" onClick={() => handleClickOpenEditForm(r)}><Ic name="edit" size={15} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length === 0 ? <div className="gos-tm-empty">{(rowData || []).length ? 'No UI permissions match your filters.' : 'No UI permissions yet — click New UI Permission.'}</div> : null}
            </>
          )}
        </div>
      </div>

      <AddUiPermissionsForm
        handleCloseAddForm={handleCloseAddForm}
        handleFormSubmit={handleFormSubmit}
        handleClickOpenEditForm={handleClickOpenEditForm}
        uiPermissionData={uiPermissionData}
        open={open}
        permissionType={permissionType}
        permissionModule={permissionModule}
        permissionParent={permissionParent}
        masterPermissionValues={[]}
        masterPermissionNames={[]}
      />
    </Wrap>
  );
}

export default UiPermissionsList;
